import type { CreateTicketResponse } from 'interfaces/zendesk';
import type { SettingsProps, TicketField, ZAFClient } from './interface';

// TODO: COLOCAR TODAS AS INTERFACES DESSE ARQUIVO EM UM ÚNICO LUGAR

declare global {
  interface Window {
    zafClient: ZAFClient;
    initializedClient?: { _origin: string };
  }
}
export let zafClient = (window as Window).zafClient;

export const loadZendeskSdk = async (): Promise<void> => {
  if (window.zafClient) {
    zafClient = window.zafClient;
    return;
  }

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://static.zdassets.com/zendesk_app_framework_sdk/2.0/zaf_sdk.min.js';
    script.async = true;
    script.onload = () => {
      window.zafClient = (
        window as typeof window & {
          ZAFClient: { init: () => unknown };
        }
      ).ZAFClient.init() as unknown as ZAFClient;
      zafClient = window.zafClient!;
      resolve();
    };
    script.onerror = () => reject(new Error('Falha ao carregar o Zendesk SDK.'));
    document.head.appendChild(script);
  });
};

export const initializeZafClient = async (): Promise<void> => {
  if (zafClient) {
    return;
  }

  await loadZendeskSdk();

  if (!window.zafClient) {
    throw new Error('Zendesk client não encontrado após carregar o SDK.');
  }

  zafClient = window.zafClient;
};

export const getApiUrl = async () => {
  const client = zafClient;
  const metadata = await client.metadata();

  const correiosUser = metadata.settings?.['correios-user'] as string;
  const correiosPassword = metadata.settings?.['correios-password'] as string;
  const correiosUrl = metadata.settings?.['correios-url'] as string;

  return {
    correiosUrl,
    correiosUser,
    correiosPassword,
  };
};

export const getMetadata = async () => {
  const client = zafClient;
  const metadata = await client.metadata();
  const metadataContent: SettingsProps = {
    'correios-user': metadata.settings['correios-user'],
    'correios-password': metadata.settings['correios-password'],
    'correios-url': metadata.settings['correios-url'],
  };

  return metadataContent;
};

export function popupError(string: string, sticky: boolean) {
  zafClient.invoke('notify', string, 'error', { sticky });
}

export function popupSuccess(string: string, sticky: boolean) {
  zafClient.invoke('notify', string, 'success', { sticky });
}

let cachedTicketFields: TicketField[] | null = null;

export async function getAllTicketFields() {
  if (cachedTicketFields) return cachedTicketFields;

  const url = '/api/v2/ticket_fields';
  const resp = await zafClient.request({
    type: 'GET',
    url,
  });

  cachedTicketFields = (resp as { ticket_fields: TicketField[] })['ticket_fields'];
  return cachedTicketFields;
}

export async function getTicketFieldValue(fieldId: number) {
  const fieldName = `custom_field_${fieldId}`;
  const response = await zafClient.get(`ticket.customField:${fieldName}`);
  return response[`ticket.customField:${fieldName}`];
}

export async function getCurrentTicketNumber(zafClient: ZAFClient) {
  try {
    const ticket = await zafClient.get('ticket');
    return (ticket as unknown as { ticket: { id: number } }).ticket.id;
  } catch (error) {
    console.error('Error getting ticket number:', error);
    return null;
  }
}

type FieldsResponse = TicketField;

interface TicketForm {
  id: number;
  name: string;
  display_name: string;
  ticket_field_ids: number[];
}

async function getFormFieldsData(
  zafClient: ZAFClient,
  form: TicketForm,
  customFields: Record<number, unknown> | Array<{ id: number; value: unknown }>,
  allTicketFields: FieldsResponse[],
  isCurrentForm: boolean = false
) {
  const formFields = allTicketFields.filter((field: FieldsResponse) =>
    form.ticket_field_ids.includes(field.id)
  );

  const excludedFieldTypes = [
    'subject',
    'description',
    'status',
    'group',
    'assignee',
    'custom_status',
  ];

  const fieldList: Array<{
    id: number;
    name: string;
    type: string;
    value: string;
    display_value: string;
    form_id: number;
  } | null> = await Promise.all(
    formFields.map(async (field: FieldsResponse) => {
      if (excludedFieldTypes.includes(field.type)) return null;

      const fieldValue = Array.isArray(customFields)
        ? (customFields.find((f: { id: number; value: unknown }) => f.id === field.id)?.value ??
          null)
        : (customFields[field.id] ?? null);

      let displayValue = fieldValue;
      if (field.type === 'tagger' && fieldValue) {
        displayValue = await getTaggerDisplayValue(zafClient, field.id, fieldValue);
      }

      return {
        id: field.id,
        name: field.title || field.raw_title,
        type: field.type,
        value: fieldValue,
        display_value: displayValue,
        form_id: form.id,
      };
    })
  );

  return {
    form: {
      id: form.id,
      name: form.name,
      display_name: form.display_name,
      current: isCurrentForm,
      ticketNumber: isCurrentForm ? await getCurrentTicketNumber(zafClient) : undefined,
    },
    fields: fieldList.filter(Boolean),
  };
}

interface CustomFieldOption {
  id: number;
  name: string;
  value: string;
}

async function getTaggerDisplayValue(zafClient: ZAFClient, fieldId: number, fieldValue: string) {
  try {
    const taggerFieldResponse = (await zafClient.request({
      url: `/api/v2/ticket_fields/${fieldId}`,
      type: 'GET',
    })) as { ticket_field: { custom_field_options?: CustomFieldOption[] } };

    const tagOption = taggerFieldResponse.ticket_field.custom_field_options?.find(
      (opt: CustomFieldOption) => opt.value === fieldValue
    );

    return tagOption?.name ?? fieldValue;
  } catch (error) {
    console.error(`Erro ao buscar opções do campo tagger ${fieldId}:`, error);
    return fieldValue;
  }
}

interface AuditEvent {
  type: string;
  field_name?: string;
  value?: string | number;
}

interface Audit {
  events?: AuditEvent[];
}

async function getHistoricalFormIds(zafClient: ZAFClient, ticketNumber: number) {
  const auditsResponse = (await zafClient.request({
    url: `/api/v2/tickets/${ticketNumber}/audits`,
    type: 'GET',
  })) as { audits: Audit[] };
  const formIds = new Set<number>();

  auditsResponse.audits.forEach((audit: Audit) => {
    audit.events?.forEach((event: AuditEvent) => {
      if (event.type === 'Change' && event.field_name === 'ticket_form_id' && event.value) {
        formIds.add(Number(event.value));
      }
    });
  });

  return Array.from(formIds);
}

export async function getAllTicketFormsData(zafClient: ZAFClient, ticketNumber: number) {
  try {
    const ticketResponse = (await zafClient.request({
      url: `/api/v2/tickets/${ticketNumber}?include=form`,
      type: 'GET',
    })) as { ticket: { ticket_form_id: number; custom_fields?: Record<string, unknown> } };

    const currentFormId = ticketResponse.ticket.ticket_form_id;
    if (!currentFormId) {
      popupError('Ticket não possui formulário associado.', false);
      return null;
    }

    const historicalFormIds = await getHistoricalFormIds(zafClient, ticketNumber);
    const allFormIds = [...new Set([currentFormId, ...historicalFormIds])];

    const formsResponse = (await zafClient.request({
      url: `/api/v2/ticket_forms?ids=${allFormIds.join(',')}`,
      type: 'GET',
    })) as { ticket_forms: TicketForm[] };

    const fieldsResponse = (await zafClient.request({
      url: `/api/v2/ticket_fields`,
      type: 'GET',
    })) as { ticket_fields: FieldsResponse[] };

    const allFormsData = await Promise.all(
      formsResponse.ticket_forms.map(async (form: TicketForm) => {
        return await getFormFieldsData(
          zafClient,
          form,
          ticketResponse.ticket.custom_fields || {},
          fieldsResponse.ticket_fields,
          form.id === currentFormId
        );
      })
    );

    return allFormsData;
  } catch (error) {
    handleFormError(error);
    return null;
  }
}

function handleFormError(error: unknown) {
  if (error instanceof Error) {
    popupError(error.message, false);
  } else {
    popupError('Erro desconhecido ao processar formulários.', false);
  }
}

export async function addInternalComment(message: string): Promise<void> {
  try {
    const ticketData = await zafClient.get('ticket');
    const ticketId = (ticketData as unknown as { ticket: { id: number } }).ticket.id;

    await zafClient.request({
      url: `/api/v2/tickets/${ticketId}.json`,
      type: 'PUT',
      data: {
        ticket: {
          comment: {
            body: message,
            public: false,
          },
        },
      },
    });

    popupSuccess('Comentário interno adicionado com sucesso!', false);
  } catch (error) {
    console.error('Erro ao adicionar comentário interno:', error);
    popupError('Erro ao adicionar comentário interno.', false);
  }
}

export interface IZendeskCurrentUser {
  alias: string;
  avatarUrl: string;
  details: string;
  email: string;
  externalId: string | null;
  id: number;
  identities: unknown[];
  isSystemUser: boolean;
  locale: string;
  name: string;
  notes: string;
  organizations: unknown[];
  role: string;
  signature: string;
  tags: string[];
  timeZone: {
    name: string;
    translatedName: string;
    ianaName: string;
    offset: number;
    formattedOffset: string;
  };
  groups: {
    id: number;
    name: string;
  }[];
}

export async function getCurrentUserName() {
  try {
    const { currentUser } = await zafClient.get('currentUser');
    return currentUser as IZendeskCurrentUser;
  } catch (error) {
    console.error('Erro ao obter usuário atual do Zendesk:', error);
    return {
      alias: '',
      avatarUrl: '',
      details: '',
      email: '',
      externalId: null,
      id: 0,
      identities: [],
      isSystemUser: false,
      locale: '',
      name: 'Usuário desconhecido',
      notes: '',
      organizations: [],
      role: '',
      signature: '',
      tags: [],
      timeZone: {
        name: '',
        translatedName: '',
        ianaName: '',
        offset: 0,
        formattedOffset: '',
      },
      groups: [],
    };
  }
}

export async function getInternalComments(zafClient: any) {
  const ticketData = await zafClient.get('ticket');
  const ticketId = (ticketData as unknown as { ticket: { id: number } }).ticket.id;

  const res = await zafClient.request({
    url: `/api/v2/tickets/${ticketId}/comments.json?sort_order=desc`,
    type: 'GET',
  });

  const internos = (res.comments || []).filter((c: any) => c.public === false);

  if (!internos.length) return null;

  const lastComment = internos[0];
  const match = lastComment.body.match(/área\s+(.+?),\s*usuário/i);
  const area = match ? match[1].trim() : null;

  return {
    ...lastComment,
    area,
  };
}

export const getZendeskUrl = () => {
  const zendeskUrl =
    (window.location.ancestorOrigins === undefined
      ? (window as Window).initializedClient?._origin
      : window.location.ancestorOrigins[0]) || '';

  return zendeskUrl;
};

// type TicketShow = {
//   ticket: { id: number; type?: string; brand_id?: number; problem_id?: number };
// };

// async function getCurrentTicketId(): Promise<number> {
//   const d = await zafClient.get('ticket.id');
//   return Number(d['ticket.id']);
// }

// async function fetchTicket(id: number) {
//   return (await zafClient.request({
//     url: `/api/v2/tickets/${id}.json`,
//     type: 'GET',
//   })) as TicketShow;
// }

// export async function resolveProblemAndBrand(mainTicketId?: number | string): Promise<{
//   problemId?: number;
//   brandIdToUse?: number;
// }> {
//   const mainId = Number(mainTicketId);
//   if (mainTicketId && Number.isFinite(mainId)) {
//     try {
//       const r = await fetchTicket(mainId);
//       if (r.ticket?.type === 'problem') {
//         return { problemId: r.ticket.id, brandIdToUse: r.ticket.brand_id };
//       }
//     } catch {}
//   }

//   const currentId = await getCurrentTicketId();
//   const cur = await fetchTicket(currentId);

//   if (cur.ticket?.type === 'problem') {
//     return { problemId: cur.ticket.id, brandIdToUse: cur.ticket.brand_id };
//   }

//   if (cur.ticket?.problem_id) {
//     try {
//       const prob = await fetchTicket(cur.ticket.problem_id);
//       if (prob.ticket?.type === 'problem') {
//         return { problemId: prob.ticket.id, brandIdToUse: prob.ticket.brand_id };
//       }
//     } catch {}
//   }

//   return {};
// }

interface ICreateTicket {
  codigoObjeto: string;
  tipoPostagem?: string;
  servico: string;
  // tipoRegistro: string;
  valorProduto: string;
  postagemReversa: boolean;
  codigoDevolucao?: string;
  motivoDevolucao?: string;
  dataSolicitacao?: string;
  prazoCorreios: string;
  rastreioDevolucao: string;
  statusPostagem: string;
  observacaoColeta?: string;
  notaFical?: string;
  estabelecimento?: string;
  serie: string;
  produto: string;
  tipoEnvioPostagem?: string;
  campoFormaEntrada?: string;
  codigoSolicitacao?: string;
  codigoProduto?: string;
  quantidade?: string;
  statusIntegração?: string;
  tipoDevolucao?: string;
  statusDevolucao?: string;
}

interface ICreateShipmentTicket {
  body: ICreateTicket;
  description: string;
  mainTicketId?: number | string;
  fallbackBrandId?: number;
  requesterId: number;
  productName?: string;
  agentId: number;
}

// ==== CONSTS (ajuste se necessário) ====
const FORM_ID = 38372911845396;
const FIELD_COD_RASTREIO = 38372417820436;
const FIELD_LOOKUP_PRODUTO = 38546069276180; // 45751139135892

// ==== HELPERS DE DATA ====
const toIsoDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// ==== RESOLVE PROBLEM + BRAND (mantém sua lógica) ====
type TicketShow = {
  ticket: { id: number; type?: string; brand_id?: number; problem_id?: number };
};

async function getCurrentTicketId(): Promise<number> {
  const d = await zafClient.get('ticket.id');
  return Number(d['ticket.id']);
}
async function fetchTicket(id: number) {
  return (await zafClient.request({
    url: `/api/v2/tickets/${id}.json`,
    type: 'GET',
  })) as TicketShow;
}
export async function resolveProblemAndBrand(mainTicketId?: number | string): Promise<{
  problemId?: number;
  brandIdToUse?: number;
  brandName?: string;
}> {
  // Captura a brand diretamente via ZAF (fonte confiável no contexto sidebar)
  // Usado como fallback garantido caso a API REST não retorne brand_id
  const getSidebarBrand = async (): Promise<{ id?: number; name?: string }> => {
    try {
      const data = await zafClient.get('ticket.brand');
      const brand = data['ticket.brand'] as { id?: number; name?: string } | undefined;
      return { id: brand?.id, name: brand?.name };
    } catch {
      return {};
    }
  };

  const getBrandName = async (brandId?: number) => {
    if (!brandId) return undefined;
    try {
      const r = await zafClient.request({
        url: `/api/v2/brands/${brandId}.json`,
        type: 'GET',
      });
      return (r as any).brand?.name as string | undefined;
    } catch {
      return undefined;
    }
  };

  const resolveFromTicket = async (
    ticket?: TicketShow['ticket']
  ): Promise<{ problemId?: number; brandIdToUse?: number; brandName?: string }> => {
    if (!ticket) return {};

    if (ticket.type === 'problem') {
      const brandId = ticket.brand_id;
      return {
        problemId: ticket.id,
        brandIdToUse: brandId,
        brandName: await getBrandName(brandId),
      };
    }

    if (ticket.problem_id) {
      try {
        const problem = await fetchTicket(ticket.problem_id);
        if (problem.ticket?.type === 'problem') {
          const brandId = problem.ticket.brand_id;
          return {
            problemId: problem.ticket.id,
            brandIdToUse: brandId,
            brandName: await getBrandName(brandId),
          };
        }
      } catch {
        return {};
      }
    }

    return {};
  };

  const mainId = Number(mainTicketId);
  if (mainTicketId && Number.isFinite(mainId)) {
    try {
      const r = await fetchTicket(mainId);
      const resolved = await resolveFromTicket(r.ticket);
      if (resolved.problemId || resolved.brandIdToUse) return resolved;
    } catch {}
  }

  const currentId = await getCurrentTicketId();
  const cur = await fetchTicket(currentId);
  const resolvedCurrent = await resolveFromTicket(cur.ticket);
  if (resolvedCurrent.problemId || resolvedCurrent.brandIdToUse) return resolvedCurrent;

  // Fallback: brand do ticket atual via ZAF (confiável no sidebar, não depende de problem)
  const sidebarBrand = await getSidebarBrand();
  if (sidebarBrand.id) {
    console.warn(
      '[resolveProblemAndBrand] brand_id obtido via ZAF sidebar fallback:',
      sidebarBrand
    );
    return {
      brandIdToUse: sidebarBrand.id,
      brandName: sidebarBrand.name,
    };
  }

  console.error('[resolveProblemAndBrand] Não foi possível determinar brand_id por nenhum meio.');
  return {};
}

type TicketFieldShow = { ticket_field: { relationship_target_type?: string } };
type BrandsListResponse = { brands?: Array<{ id: number; name: string }> };
type CORecord = {
  id: string;
  external_id?: string;
  properties?: Record<string, any>;
  name?: string;
};
type CORecordsSearchGET = { records?: CORecord[] };

async function getCurrentLookupRecordId(fieldId: number): Promise<string | undefined> {
  const key = `ticket.customField:custom_field_${fieldId}`;
  const data = await zafClient.get(key);
  const raw = (data as any)[key];
  return typeof raw === 'string' && raw.trim() ? raw.trim() : undefined;
}

async function getLookupTargetKey(fieldId: number) {
  const r = (await zafClient.request({
    url: `/api/v2/ticket_fields/${fieldId}.json`,
    type: 'GET',
  })) as TicketFieldShow;
  const rtt = r.ticket_field.relationship_target_type || '';
  const key = rtt.startsWith('zen:custom_object:') ? rtt.split(':').pop() : undefined;
  if (!key) throw new Error('Campo lookup sem relationship_target_type.');
  return key;
}

async function findCORecordIdByValue(objectKey: string, text: string) {
  const v = (text ?? '').trim();
  if (!v) return undefined;

  const q1 = encodeURIComponent(`custom_object_key:${objectKey} name:"${v}"`);
  let res = (await zafClient.request({
    url: `/api/v2/custom_objects/records/search?query=${q1}`,
    type: 'GET',
  })) as CORecordsSearchGET;
  if (res.records?.[0]?.id) return res.records[0].id;

  const props = [
    'sku',
    'produto_comercial',
    'referencia_telecontrol',
    'codigo_erp',
    'codigo',
    'modelo',
  ];
  for (const p of props) {
    const q = encodeURIComponent(`custom_object_key:${objectKey} properties.${p}:"${v}"`);
    res = (await zafClient.request({
      url: `/api/v2/custom_objects/records/search?query=${q}`,
      type: 'GET',
    })) as CORecordsSearchGET;
    if (res.records?.[0]?.id) return res.records[0].id;
  }
  return undefined;
}

async function resolveProductLookupId(fieldId: number, productText?: string) {
  const fromCurrent = await getCurrentLookupRecordId(fieldId);
  if (fromCurrent) return fromCurrent;

  const key = await getLookupTargetKey(fieldId);
  return findCORecordIdByValue(key, productText ?? '');
}

const normalizeBrandName = (value?: string) =>
  (value ?? '')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toUpperCase()
    .trim();

const getBrandSearchTermsByArea = (area?: string) => {
  switch (area) {
    case 'AC':
      return ['JURIDICO'];
    case 'SAC AT':
      return ['SAC AT'];
    case 'SAC E-COM':
      return ['SAC ECOM', 'SAC E-COM'];
    default:
      return [];
  }
};

export async function resolveBrandIdByArea(
  area?: string,
  currentBrand?: { id?: number; name?: string }
): Promise<number | undefined> {
  const searchTerms = getBrandSearchTermsByArea(area);
  if (searchTerms.length === 0) return undefined;

  const currentBrandName = normalizeBrandName(currentBrand?.name);
  if (currentBrand?.id && searchTerms.some((term) => currentBrandName.includes(term))) {
    return currentBrand.id;
  }

  try {
    const response = (await zafClient.request({
      url: '/api/v2/brands.json',
      type: 'GET',
    })) as BrandsListResponse;

    const matchedBrand = response.brands?.find((brand) => {
      const normalizedBrandName = normalizeBrandName(brand.name);
      return searchTerms.some((term) => normalizedBrandName.includes(term));
    });

    return matchedBrand?.id;
  } catch {
    return currentBrand?.id;
  }
}

export async function getCurrentTicketBrand(): Promise<{ id?: number; name?: string }> {
  try {
    const data = await zafClient.get('ticket.brand');
    const brand = data['ticket.brand'] as { id?: number; name?: string } | undefined;
    return { id: brand?.id, name: brand?.name };
  } catch {
    return {};
  }
}

export async function createTicket({
  body,
  description,
  mainTicketId,
  fallbackBrandId,
  requesterId,
  productName,
  agentId,
}: ICreateShipmentTicket) {
  if (!body.codigoObjeto) throw new Error('codigoObjeto vazio');

  const { problemId, brandIdToUse } = await resolveProblemAndBrand(mainTicketId);
  // Prioridade: seleção do usuário na Home (fallbackBrandId) > resolução automática pelo ticket
  const resolvedBrandId = fallbackBrandId ?? brandIdToUse;

  if (!resolvedBrandId) {
    console.warn(
      '[createTicket] Todas as tentativas de resolver brand_id falharam; criando incident sem brand_id.'
    );
  }

  const productRecordId = await resolveProductLookupId(
    FIELD_LOOKUP_PRODUTO,
    body.produto ?? productName
  );

  const custom_fields: Array<{ id: number; value: any }> = [
    { id: FIELD_COD_RASTREIO, value: body.codigoObjeto },
    {
      id: 38378192293268,
      value:
        body.tipoPostagem === 'A'
          ? 'a___autorização_de_postagem'
          : body.tipoPostagem === 'CA'
            ? 'ca___coleta_domiciliar'
            : '',
    },
    {
      id: 38375455350420,
      value: body.servico === '03298' || body.servico === '03301' ? 'pac' : 'sedex',
    },
    { id: 38373007208340, value: 'correios_lgr_layout' },
    { id: 38375898555796, value: body.valorProduto },
    { id: 38375377664148, value: body.postagemReversa ? true : false },
    { id: 38374263613844, value: body.codigoDevolucao },
    { id: 38375149016980, value: body.motivoDevolucao },
    { id: 38374386331796, value: toIsoDate(new Date()) },
    { id: 38375383498260, value: body.prazoCorreios || '' },
    { id: 38375408016788, value: body.rastreioDevolucao },
    { id: 38375718268692, value: body.statusPostagem },
    { id: 38377758666900, value: body.observacaoColeta },
    { id: 38375250215188, value: body.notaFical },
    { id: 38374638222996, value: body.estabelecimento },
    { id: 38375412352532, value: body.serie },
    { id: 38374625151636, value: body.campoFormaEntrada },
    { id: 38374289099156, value: body.codigoSolicitacao },
    { id: 43486241182612, value: body.statusIntegração },
    { id: 38379168866068, value: body.tipoDevolucao },
    { id: 44502958166676, value: body.statusDevolucao },
    { id: 38373031575700, value: body.codigoProduto },
    { id: 40834654892436, value: body.quantidade },
    {
      id: 38379663225748,
      value: body.tipoEnvioPostagem,
    },
    ...(productRecordId ? [{ id: FIELD_LOOKUP_PRODUTO, value: productRecordId }] : []),
  ];

  const payload = {
    ticket: {
      subject: `LGR ${productName}`,
      type: 'incident',
      requester_id: requesterId,
      priority: 'low',
      ticket_form_id: FORM_ID,
      ...(resolvedBrandId ? { brand_id: resolvedBrandId } : {}),
      ...(problemId ? { problem_id: problemId } : {}),
      comment: {
        body: description,
        public: false,
        author_id: agentId, // 👈 aqui entra o ID que você já tem
      },

      custom_fields,
    },
  };

  const res = await (zafClient.request as any)({
    url: '/api/v2/tickets.json',
    type: 'POST',
    contentType: 'application/json',
    data: JSON.stringify(payload),
  });

  return res as CreateTicketResponse;
}
